import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react'
import { EditableKanaModalBody } from '../../../../components/templates/EditableKanaModalBody'
import { divideNameIntoFirstAndLast } from '../../../../helpers/divideNameIntoFirstAndLast'
import { BasicResult, EditBasicResultKey } from '../../../../types/models/Presc'
import { useModal } from '../../../../shared'

export const useKanaEditModal = (
  prescId: string,
  basicResult: BasicResult,
  editBasicResult: (key: EditBasicResultKey) => void
) => {
  // 初期値を計算
  const initialFirstName = useMemo(
    () =>
      divideNameIntoFirstAndLast(
        basicResult.id_11_patient_name_kana.selected_option
      ).first,
    [basicResult.id_11_patient_name_kana.selected_option]
  )

  const initialLastName = useMemo(
    () =>
      divideNameIntoFirstAndLast(
        basicResult.id_11_patient_name_kana.selected_option
      ).last,
    [basicResult.id_11_patient_name_kana.selected_option]
  )

  // prescIdをキーにして、prescId変更時に初期値を適用
  const [nameState, setNameState] = useState<{
    prescId: string
    firstName: string
    lastName: string
  }>({
    prescId: '',
    firstName: initialFirstName,
    lastName: initialLastName,
  })

  const firstName =
    prescId === nameState.prescId ? nameState.firstName : initialFirstName
  const lastName =
    prescId === nameState.prescId ? nameState.lastName : initialLastName

  const setFirstName = useCallback(
    (value: string) => {
      setNameState((prev) => ({ ...prev, prescId, firstName: value }))
    },
    [prescId]
  )

  const setLastName = useCallback(
    (value: string) => {
      setNameState((prev) => ({ ...prev, prescId, lastName: value }))
    },
    [prescId]
  )

  const firstNameList = useMemo(
    () =>
      Array.from(
        new Set(
          basicResult.id_11_patient_name_kana.options.map(
            (name) => divideNameIntoFirstAndLast(name).first
          )
        )
      ),
    [basicResult.id_11_patient_name_kana.options]
  )

  const lastNameList = useMemo(
    () =>
      Array.from(
        new Set(
          basicResult.id_11_patient_name_kana.options.map(
            (name) => divideNameIntoFirstAndLast(name).last
          )
        )
      ),
    [basicResult.id_11_patient_name_kana.options]
  )

  const closeEditableModalRef = useRef<() => void>(() => {})

  const completeEdit = useCallback(() => {
    editBasicResult({
      key: 'id_11_patient_name_kana',
      value: [firstName, lastName].join(' '),
    })
    closeEditableModalRef.current()
  }, [editBasicResult, firstName, lastName])

  const {
    openModal: openEditableModal,
    closeModal: closeEditableModal,
    renderModal: renderEditableModal,
  } = useModal(
    <EditableKanaModalBody
      firstNameList={firstNameList}
      lastNameList={lastNameList}
      initialFirstName={initialFirstName}
      firstName={firstName}
      initialLastName={initialLastName}
      lastName={lastName}
      setFirstName={setFirstName}
      setLastName={setLastName}
      completeEdit={completeEdit}
    />,
    'right'
  )

  useEffect(() => {
    closeEditableModalRef.current = closeEditableModal
  }, [closeEditableModal])

  return { openEditableModal, renderEditableModal }
}
